import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TextInput, TouchableOpacity, Alert } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../types/navigation';
import { Icon } from '../components/Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';

type NavigationProp = StackNavigationProp<RootStackParamList, 'M06InputCustomerScreen'>;

export const M06InputCustomerScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const [step, setStep] = useState(1);
  const [isEditingOwner, setIsEditingOwner] = useState(false);

  // Policy Owner States (matching 04_chon_ben_mua_bao_hiem)
  const [ownerFirstName, setOwnerFirstName] = useState('Luận');
  const [ownerLastName, setOwnerLastName] = useState('Tạ');
  const [ownerMiddleName, setOwnerMiddleName] = useState('Minh');
  const [ownerGender, setOwnerGender] = useState<'Nam' | 'Nữ'>('Nam');
  const [ownerDob, setOwnerDob] = useState('25/11/1996');
  const [ownerAge, setOwnerAge] = useState(29);
  const [ownerOccupation, setOwnerOccupation] = useState('Hành chính - Lập trình viên/Phân tích viên/Quản trị mạng');
  const [ownerSmoking, setOwnerSmoking] = useState<'never' | 'light' | 'heavy'>('never');

  // Temporary States for Editing Owner Form
  const [tempFirstName, setTempFirstName] = useState(ownerFirstName);
  const [tempLastName, setTempLastName] = useState(ownerLastName);
  const [tempMiddleName, setTempMiddleName] = useState(ownerMiddleName);
  const [tempGender, setTempGender] = useState(ownerGender);
  const [tempDob, setTempDob] = useState(ownerDob);
  const [tempOccupation, setTempOccupation] = useState(ownerOccupation);
  const [tempSmoking, setTempSmoking] = useState(ownerSmoking);

  // Insured Selection States (matching 03_cac_buoc)
  const [selectedInsured, setSelectedInsured] = useState('Tạ Minh Luận');
  const [extraInsureds, setExtraInsureds] = useState<string[]>([]);

  // Step 2 State: Policy config
  const [sumAssured, setSumAssured] = useState('1.000.000.000');
  const [term, setTerm] = useState('20 năm');
  const [freq, setFreq] = useState('Hàng năm');
  const [estPremium, setEstPremium] = useState('15.209.000');

  // Calculate age when DoB changes
  const calculateAge = (dobString: string) => {
    try {
      const parts = dobString.split('/');
      if (parts.length === 3) {
        const birthYear = parseInt(parts[2], 10);
        const currentYear = new Date().getFullYear();
        if (!isNaN(birthYear) && birthYear > 1900 && birthYear <= currentYear) {
          return currentYear - birthYear;
        }
      }
    } catch (e) {}
    return 29; // default fallback
  };

  const startEditingOwner = () => {
    setTempFirstName(ownerFirstName);
    setTempLastName(ownerLastName);
    setTempMiddleName(ownerMiddleName);
    setTempGender(ownerGender);
    setTempDob(ownerDob);
    setTempOccupation(ownerOccupation);
    setTempSmoking(ownerSmoking);
    setIsEditingOwner(true);
  };

  const saveOwnerDetails = () => {
    if (!tempFirstName.trim() || !tempLastName.trim()) {
      Alert.alert('Lỗi', 'Vui lòng nhập đầy đủ Họ và Tên.');
      return;
    }
    setOwnerFirstName(tempFirstName);
    setOwnerLastName(tempLastName);
    setOwnerMiddleName(tempMiddleName);
    setOwnerGender(tempGender);
    setOwnerDob(tempDob);
    setOwnerOccupation(tempOccupation);
    setOwnerSmoking(tempSmoking);
    
    // Calculate new age
    setOwnerAge(calculateAge(tempDob));

    // Update active insured name if it was the owner
    if (selectedInsured === `${ownerLastName} ${ownerMiddleName} ${ownerFirstName}`.trim()) {
      setSelectedInsured(`${tempLastName} ${tempMiddleName} ${tempFirstName}`.trim());
    }

    setIsEditingOwner(false);
  };

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      navigation.navigate('M07ResultScreen', { illustrationId: 'mock-id-123' });
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      navigation.goBack();
    }
  };

  const handleAddInsured = () => {
    Alert.prompt(
      'Thêm Người Được Bảo Hiểm Phụ',
      'Nhập họ tên người được bảo hiểm mới:',
      [
        { text: 'Hủy', style: 'cancel' },
        { 
          text: 'Thêm', 
          onPress: (name) => {
            if (name && name.trim()) {
              setExtraInsureds([...extraInsureds, name.trim()]);
            }
          } 
        }
      ]
    );
  };

  const getOwnerFullName = () => {
    return `${ownerLastName} ${ownerMiddleName} ${ownerFirstName}`.trim();
  };

  return (
    <View style={styles.container}>
      {/* Step Indicator */}
      {!isEditingOwner && (
        <View style={styles.stepHeader}>
          <Text style={styles.stepText}>Bước {step}/3</Text>
          <View style={styles.indicatorRow}>
            <View style={[styles.indicatorLine, step >= 1 && styles.indicatorActive]} />
            <View style={[styles.indicatorLine, step >= 2 && styles.indicatorActive]} />
            <View style={[styles.indicatorLine, step >= 3 && styles.indicatorActive]} />
          </View>
        </View>
      )}

      <ScrollView contentContainerStyle={styles.scrollContent}>
        {step === 1 && (
          <View style={styles.stepContent}>
            {isEditingOwner ? (
              /* POLICY OWNER DETAIL EDITING FORM (04_chon_ben_mua_bao_hiem) */
              <View style={styles.formContainer}>
                <Text style={styles.formTitle}>Bên Mua Bảo Hiểm</Text>
                
                <View style={styles.infoBanner}>
                  <Icon name="info" size={18} color={colors.primary} />
                  <Text style={styles.infoBannerText}>Họ tên đầy đủ có dấu</Text>
                </View>

                <View style={styles.nameRow}>
                  <View style={[styles.inputContainer, { flex: 1 }]}>
                    <Text style={styles.label}>Tên *</Text>
                    <TextInput 
                      style={styles.input} 
                      value={tempFirstName} 
                      onChangeText={setTempFirstName} 
                      placeholder="Nhập tên"
                    />
                  </View>
                  <View style={[styles.inputContainer, { flex: 1 }]}>
                    <Text style={styles.label}>Họ *</Text>
                    <TextInput 
                      style={styles.input} 
                      value={tempLastName} 
                      onChangeText={setTempLastName} 
                      placeholder="Nhập họ"
                    />
                  </View>
                </View>

                <View style={styles.inputContainer}>
                  <Text style={styles.label}>Tên đệm</Text>
                  <TextInput 
                    style={styles.input} 
                    value={tempMiddleName} 
                    onChangeText={setTempMiddleName} 
                    placeholder="Nhập tên đệm"
                  />
                </View>

                <View style={styles.inputContainer}>
                  <Text style={styles.label}>Giới tính *</Text>
                  <View style={styles.genderSelectRow}>
                    <TouchableOpacity
                      style={[styles.genderSelectBtn, tempGender === 'Nam' && styles.genderSelectActive]}
                      onPress={() => setTempGender('Nam')}
                    >
                      <Icon name="male" size={20} color={tempGender === 'Nam' ? '#00A651' : colors.onSurfaceVariant} />
                      <Text style={[styles.genderSelectText, tempGender === 'Nam' && styles.genderSelectTextActive]}>Nam</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.genderSelectBtn, tempGender === 'Nữ' && styles.genderSelectActive]}
                      onPress={() => setTempGender('Nữ')}
                    >
                      <Icon name="female" size={20} color={tempGender === 'Nữ' ? '#00A651' : colors.onSurfaceVariant} />
                      <Text style={[styles.genderSelectText, tempGender === 'Nữ' && styles.genderSelectTextActive]}>Nữ</Text>
                    </TouchableOpacity>
                  </View>
                </View>

                <View style={styles.nameRow}>
                  <View style={[styles.inputContainer, { flex: 2 }]}>
                    <Text style={styles.label}>Ngày tháng năm sinh *</Text>
                    <View style={styles.inputIconWrapper}>
                      <TextInput 
                        style={styles.inputWithIcon} 
                        value={tempDob} 
                        onChangeText={setTempDob} 
                        placeholder="DD/MM/YYYY"
                      />
                      <Icon name="calendar_today" size={18} color={colors.onSurfaceVariant} style={styles.rightIcon} />
                    </View>
                  </View>
                  <View style={[styles.inputContainer, { flex: 1 }]}>
                    <Text style={styles.label}>Tuổi</Text>
                    <Text style={styles.readOnlyAge}>{calculateAge(tempDob)}</Text>
                  </View>
                </View>

                <View style={styles.inputContainer}>
                  <Text style={styles.label}>Nghề Nghiệp *</Text>
                  <View style={styles.occupationWrapper}>
                    <View style={styles.occupationPill}>
                      <Text style={styles.occupationText} numberOfLines={1}>
                        {tempOccupation}
                      </Text>
                    </View>
                    <TouchableOpacity style={styles.searchIconBtn}>
                      <Icon name="search" size={20} color={colors.onSurface} />
                    </TouchableOpacity>
                  </View>
                </View>

                <View style={styles.inputContainer}>
                  <Text style={styles.label}>Thói quen hút thuốc *</Text>
                  <View style={styles.segmentedControl}>
                    <TouchableOpacity
                      style={[styles.segmentBtn, tempSmoking === 'never' && styles.segmentActive]}
                      onPress={() => setTempSmoking('never')}
                    >
                      <Text style={[styles.segmentText, tempSmoking === 'never' && styles.segmentTextActive]}>Chưa bao giờ hút thuốc</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.segmentBtn, tempSmoking === 'light' && styles.segmentActive]}
                      onPress={() => setTempSmoking('light')}
                    >
                      <Text style={[styles.segmentText, tempSmoking === 'light' && styles.segmentTextActive]}>Hút &lt;= 30 điếu/ngày</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.segmentBtn, tempSmoking === 'heavy' && styles.segmentActive]}
                      onPress={() => setTempSmoking('heavy')}
                    >
                      <Text style={[styles.segmentText, tempSmoking === 'heavy' && styles.segmentTextActive]}>Hút &gt; 30 điếu/ngày</Text>
                    </TouchableOpacity>
                  </View>
                </View>

                {/* Form Buttons */}
                <View style={styles.formButtonRow}>
                  <TouchableOpacity style={styles.cancelBtn} onPress={() => setIsEditingOwner(false)}>
                    <Text style={styles.cancelBtnText}>Hủy bỏ</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.saveBtn} onPress={saveOwnerDetails}>
                    <Text style={styles.saveBtnText}>Lưu</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : (
              /* ROLE CONFIGURATION SCREEN (03_cac_buoc) */
              <View style={styles.roleContainer}>
                {/* Policy Owner Card */}
                <View style={[styles.roleCard, { borderTopColor: '#003366' }]}>
                  <View style={styles.roleCardHeader}>
                    <Text style={styles.roleCardTitle}>Bên Mua Bảo Hiểm</Text>
                    <TouchableOpacity onPress={startEditingOwner} style={styles.arrowIconBtn}>
                      <Icon name="arrow_forward" size={20} color="#EF4444" />
                    </TouchableOpacity>
                  </View>
                  <Text style={styles.ownerNameText}>{getOwnerFullName()}</Text>
                  
                  <View style={styles.ownerDetails}>
                    <View style={styles.detailItem}>
                      <Text style={styles.detailLabel}>Giới tính</Text>
                      <Text style={styles.detailValue}>{ownerGender}</Text>
                    </View>
                    <View style={styles.detailItem}>
                      <Text style={styles.detailLabel}>Tuổi</Text>
                      <Text style={styles.detailValue}>{ownerAge}</Text>
                    </View>
                    <View style={styles.detailItem}>
                      <Text style={styles.detailLabel}>Mối quan hệ với NĐBH</Text>
                      <Text style={styles.detailValue}>-</Text>
                    </View>
                  </View>
                </View>

                {/* Insured Selection Card */}
                <View style={[styles.roleCard, { borderTopColor: '#00A651', marginTop: spacing[4] }]}>
                  <Text style={styles.roleCardTitleGreen}>Vui lòng chọn lựa Người Được Bảo hiểm chính</Text>
                  
                  <View style={styles.radioList}>
                    {/* Primary Option: Self */}
                    <TouchableOpacity 
                      style={styles.radioRow} 
                      onPress={() => setSelectedInsured(getOwnerFullName())}
                    >
                      <Icon 
                        name={selectedInsured === getOwnerFullName() ? 'radio_button_checked' : 'radio_button_unchecked'} 
                        size={22} 
                        color={selectedInsured === getOwnerFullName() ? '#00A651' : '#9e9e9e'} 
                      />
                      <Text style={styles.radioLabel}>{getOwnerFullName()} (Bản thân)</Text>
                    </TouchableOpacity>

                    {/* Default relations */}
                    {['Vợ Chồng NĐBH', 'Con NĐBH', 'Ba Mẹ NĐBH', ...extraInsureds].map((relation) => (
                      <TouchableOpacity 
                        key={relation} 
                        style={styles.radioRow} 
                        onPress={() => setSelectedInsured(relation)}
                      >
                        <Icon 
                          name={selectedInsured === relation ? 'radio_button_checked' : 'radio_button_unchecked'} 
                          size={22} 
                          color={selectedInsured === relation ? '#00A651' : '#9e9e9e'} 
                        />
                        <Text style={styles.radioLabel}>{relation}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>

                  <TouchableOpacity style={styles.addInsuredBtn} onPress={handleAddInsured}>
                    <Icon name="add_circle" size={20} color="#EF4444" />
                    <Text style={styles.addInsuredText}>Thêm Người Được Bảo Hiểm</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </View>
        )}

        {step === 2 && (
          <View style={styles.stepContent}>
            <Text style={styles.productBadge}>Quyền lợi sản phẩm - NĐBH: {selectedInsured}</Text>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>💰 Số tiền bảo hiểm *</Text>
              <TextInput style={styles.input} value={sumAssured} onChangeText={setSumAssured} />
              <View style={styles.chipRow}>
                {['500tr', '1 tỷ', '2 tỷ', '3 tỷ'].map((c) => (
                  <TouchableOpacity
                    key={c}
                    style={styles.chip}
                    onPress={() => setSumAssured(c === '500tr' ? '500.000.000' : c === '1 tỷ' ? '1.000.000.000' : c === '2 tỷ' ? '2.000.000.000' : '3.000.000.000')}
                  >
                    <Text style={styles.chipText}>{c}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>📅 Thời hạn đóng phí *</Text>
              <View style={styles.termRow}>
                {['10 năm', '20 năm', 'Trọn đời'].map((t) => (
                  <TouchableOpacity
                    key={t}
                    style={[styles.termBtn, term === t && styles.termBtnActive]}
                    onPress={() => setTerm(t)}
                  >
                    <Text style={[styles.termBtnText, term === t && styles.termBtnTextActive]}>{t}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>💵 Phương thức đóng phí *</Text>
              <TextInput style={styles.input} value={freq} onChangeText={setFreq} />
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>💰 Phí đóng hàng năm ước tính</Text>
              <TextInput style={styles.input} value={estPremium} onChangeText={setEstPremium} />
            </View>

            {/* AI suggestion box */}
            <View style={styles.aiCard}>
              <View style={styles.aiHeader}>
                <Icon name="calculate" size={20} color={colors.primary} />
                <Text style={styles.aiTitle}>Gợi ý từ AI Copilot</Text>
              </View>
              <Text style={styles.aiContent}>
                Với độ tuổi {ownerAge} của NĐBH, phương án đóng phí {term} là tối ưu nhất. 
                Để đảm bảo tích lũy cho mục tiêu dài hạn, bạn có thể cân nhắc tăng thêm sản phẩm bổ trợ về sức khỏe đi kèm.
              </Text>
            </View>
          </View>
        )}

        {step === 3 && (
          <View style={styles.stepContent}>
            <Text style={styles.sectionTitle}>✅ Đã sẵn sàng tính toán</Text>
            <View style={styles.summaryCard}>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Bên mua bảo hiểm:</Text>
                <Text style={styles.summaryValue}>{getOwnerFullName()}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Người được bảo hiểm:</Text>
                <Text style={styles.summaryValue}>{selectedInsured}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Tuổi / Giới tính:</Text>
                <Text style={styles.summaryValue}>{ownerAge} tuổi / {ownerGender}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Số tiền bảo hiểm:</Text>
                <Text style={styles.summaryValue}>{sumAssured} VNĐ</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Thời hạn đóng phí:</Text>
                <Text style={styles.summaryValue}>{term}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Phí đóng ước tính:</Text>
                <Text style={styles.summaryValue}>{estPremium} VNĐ</Text>
              </View>
            </View>

            <TouchableOpacity style={styles.editBtn} onPress={() => setStep(1)}>
              <Icon name="edit" size={16} color={colors.primary} />
              <Text style={styles.editBtnText}>Sửa thông tin vai trò</Text>
            </TouchableOpacity>
          </View>
        )}
      </ScrollView>

      {/* Navigation bottom bar */}
      {!isEditingOwner && (
        <View style={styles.bottomBar}>
          <TouchableOpacity style={styles.backBtn} onPress={handleBack}>
            <Text style={styles.backBtnText}>Quay lại</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.nextBtn} onPress={handleNext}>
            <Text style={styles.nextBtnText}>{step === 3 ? 'XEM KẾT QUẢ' : 'TIẾP TỤC'}</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f7fa',
  },
  stepHeader: {
    backgroundColor: '#ffffff',
    padding: spacing[3],
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.outlineVariant,
  },
  stepText: {
    fontSize: 12,
    fontWeight: fontWeights.semibold,
    color: colors.onSurfaceVariant,
    marginBottom: spacing[2],
  },
  indicatorRow: {
    flexDirection: 'row',
    gap: spacing[2],
  },
  indicatorLine: {
    flex: 1,
    height: 4,
    backgroundColor: '#e1e2e9',
    borderRadius: 2,
  },
  indicatorActive: {
    backgroundColor: colors.secondary,
  },
  scrollContent: {
    padding: spacing[4],
    paddingBottom: 88,
  },
  stepContent: {
    gap: spacing[4],
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[2],
  },
  productBadge: {
    alignSelf: 'flex-start',
    backgroundColor: colors.primaryFixed,
    color: colors.primary,
    fontSize: 12,
    fontWeight: fontWeights.bold,
    paddingHorizontal: spacing[2],
    paddingVertical: spacing[1],
    borderRadius: borderRadius.sm,
    marginBottom: spacing[2],
  },
  inputContainer: {
    gap: spacing[1.5],
    marginBottom: spacing[1],
  },
  label: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
  input: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    paddingHorizontal: spacing[3],
    height: 44,
    fontSize: 14,
    color: colors.onSurface,
  },
  inputIconWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    height: 44,
    position: 'relative',
  },
  inputWithIcon: {
    flex: 1,
    height: '100%',
    paddingHorizontal: spacing[3],
    fontSize: 14,
    color: colors.onSurface,
  },
  rightIcon: {
    position: 'absolute',
    right: 12,
  },
  readOnlyAge: {
    backgroundColor: '#f3f4f6',
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    borderRadius: borderRadius.DEFAULT,
    paddingHorizontal: spacing[3],
    height: 44,
    lineHeight: 44,
    fontSize: 14,
    color: colors.onSurfaceVariant,
    textAlign: 'center',
    fontWeight: fontWeights.semibold,
  },
  nameRow: {
    flexDirection: 'row',
    gap: spacing[3],
  },
  termRow: {
    flexDirection: 'row',
    gap: spacing[2],
  },
  termBtn: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    backgroundColor: '#ffffff',
    height: 44,
  },
  termBtnActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  termBtnText: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurfaceVariant,
  },
  termBtnTextActive: {
    color: '#ffffff',
  },
  chipRow: {
    flexDirection: 'row',
    gap: spacing[2],
    marginTop: spacing[1],
  },
  chip: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.sm,
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[1.5],
  },
  chipText: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
  },
  aiCard: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: borderRadius.md,
    padding: spacing[4],
    gap: spacing[2],
    marginTop: spacing[2],
  },
  aiHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  aiTitle: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  aiContent: {
    fontSize: 12.5,
    lineHeight: 18,
    color: colors.onSurface,
    textAlign: 'justify',
  },
  summaryCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    gap: spacing[2.5],
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  summaryLabel: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },
  summaryValue: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
  editBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 40,
    gap: spacing[1.5],
    marginTop: spacing[2],
  },
  editBtnText: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  bottomBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#ffffff',
    flexDirection: 'row',
    padding: spacing[4],
    borderTopWidth: 1,
    borderTopColor: colors.outlineVariant,
    gap: spacing[3],
  },
  backBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backBtnText: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  nextBtn: {
    flex: 2,
    backgroundColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nextBtnText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: fontWeights.bold,
  },

  /* ROLE CONFIGURATION STYLES (03_cac_buoc) */
  roleContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[4],
  },
  roleCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderTopWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
    flex: 1,
    minWidth: 280,
  },

  roleCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing[2],
  },
  roleCardTitle: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.onSurfaceVariant,
  },
  roleCardTitleGreen: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: '#00A651',
    marginBottom: spacing[3],
  },
  arrowIconBtn: {
    padding: spacing[1],
  },
  ownerNameText: {
    fontSize: 22,
    fontWeight: fontWeights.bold,
    color: colors.primary,
    marginBottom: spacing[3],
  },
  ownerDetails: {
    gap: spacing[2],
  },
  detailItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  detailLabel: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },
  detailValue: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
  radioList: {
    gap: spacing[2.5],
    marginBottom: spacing[4],
  },
  radioRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    paddingVertical: spacing[1],
  },
  radioLabel: {
    fontSize: 14,
    color: colors.onSurface,
    fontWeight: '500',
  },
  addInsuredBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#EF4444',
    borderStyle: 'dashed',
    borderRadius: borderRadius.DEFAULT,
    height: 44,
    gap: spacing[2],
  },
  addInsuredText: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#EF4444',
  },

  /* POLICY OWNER EDITING FORM STYLES (04_chon_ben_mua_bao_hiem) */
  formContainer: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    gap: spacing[3],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
  },

  formTitle: {
    fontSize: 18,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  infoBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,51,102,0.04)',
    borderWidth: 1,
    borderColor: 'rgba(0,51,102,0.08)',
    borderRadius: borderRadius.DEFAULT,
    padding: spacing[3],
    gap: spacing[2],
  },
  infoBannerText: {
    fontSize: 12.5,
    color: colors.primary,
    fontWeight: '500',
  },
  genderSelectRow: {
    flexDirection: 'row',
    gap: spacing[3],
  },
  genderSelectBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.full,
    height: 44,
    gap: spacing[2],
    backgroundColor: '#ffffff',
  },
  genderSelectActive: {
    borderColor: '#00A651',
    borderWidth: 2,
  },
  genderSelectText: {
    fontSize: 14,
    fontWeight: fontWeights.semibold,
    color: colors.onSurfaceVariant,
  },
  genderSelectTextActive: {
    color: '#00A651',
    fontWeight: fontWeights.bold,
  },
  occupationWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  occupationPill: {
    flex: 1,
    backgroundColor: '#00A651',
    borderRadius: borderRadius.full,
    height: 40,
    justifyContent: 'center',
    paddingHorizontal: spacing[4],
  },
  occupationText: {
    color: '#ffffff',
    fontSize: 12.5,
    fontWeight: fontWeights.semibold,
  },
  searchIconBtn: {
    width: 40,
    height: 40,
    borderRadius: borderRadius.full,
    borderWidth: 1,
    borderColor: colors.outline,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
  },
  segmentedControl: {
    gap: spacing[2],
  },
  segmentBtn: {
    height: 40,
    borderRadius: borderRadius.full,
    backgroundColor: '#f3f4f6',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing[4],
  },
  segmentActive: {
    backgroundColor: '#00A651',
  },
  segmentText: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
  },
  segmentTextActive: {
    color: '#ffffff',
    fontWeight: fontWeights.semibold,
  },
  formButtonRow: {
    flexDirection: 'row',
    gap: spacing[3],
    marginTop: spacing[2],
  },
  cancelBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#EF4444',
    borderRadius: borderRadius.DEFAULT,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelBtnText: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: '#EF4444',
  },
  saveBtn: {
    flex: 1,
    backgroundColor: '#EF4444',
    borderRadius: borderRadius.DEFAULT,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveBtnText: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: '#ffffff',
  },
});
